/**
 * Search Results Page
 * 
 * Displays full search results with filtering and pagination
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, FileText, Calendar, Image, MessageSquare, BookOpen, Filter, X, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { searchService } from '@/services/searchService';
import { queryKeys } from '@/lib/query-config';
import { toast } from 'sonner';
import { safeAuth } from '@/lib/safe-supabase';
import { RichTextDisplay } from '@/components/ui/rich-text-display';
import type { SearchResult, SearchContentType, SearchOptions, SavedSearch } from '@/types/search';

const typeIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  page: FileText,
  news: FileText,
  event: Calendar,
  gallery: Image,
  testimonial: MessageSquare,
  fact: BookOpen,
};

const typeLabels: Record<string, string> = {
  page: 'Pages',
  news: 'News',
  event: 'Events',
  gallery: 'Gallery',
  testimonial: 'Testimonials',
  fact: 'Facts',
};

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [selectedType, setSelectedType] = useState<SearchContentType>('all');
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<{
    dateFrom?: string;
    dateTo?: string;
    category?: string;
    author?: string;
  }>({});
  const limit = 20;

  // Update query from URL params
  useEffect(() => {
    const urlQuery = searchParams.get('q') || '';
    setQuery(urlQuery);
  }, [searchParams]);

  // Get search suggestions
  const { data: suggestions = [] } = useQuery({
    queryKey: ['search-suggestions', query],
    queryFn: async () => {
      if (!query.trim() || query.length < 2) return [];
      const result = await searchService.getSearchSuggestions(query, 5);
      return result.success ? result.data : [];
    },
    enabled: query.trim().length >= 2,
    staleTime: 60_000,
  });

  // Search query with filters
  const searchOptions: SearchOptions = {
    contentType: selectedType === 'all' ? undefined : selectedType,
    limit,
    offset: (page - 1) * limit,
    trackAnalytics: true,
    ...filters,
  };

  const { data: results = [], isLoading } = useQuery({
    queryKey: [...queryKeys.search, query, selectedType, page, filters],
    queryFn: async () => {
      if (!query.trim()) return [];
      const result = await searchService.searchAll(query, searchOptions);
      return result.success ? result.data : [];
    },
    enabled: query.trim().length > 0,
    staleTime: 30_000,
  });

  // Get saved searches (for authenticated users)
  const [user, setUser] = useState<any>(null);
  useEffect(() => {
    const checkUser = async () => {
      try {
        const { data: { user: currentUser } } = await safeAuth.getUser();
        setUser(currentUser);
      } catch (error) {
        setUser(null);
      }
    };
    checkUser();
  }, []);

  const { data: savedSearches = [] } = useQuery({
    queryKey: ['saved-searches'],
    queryFn: async () => {
      const result = await searchService.getSavedSearches();
      return result.success ? result.data : [];
    },
    enabled: !!user,
    staleTime: 60_000,
  });

  // Group results by type
  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = [];
    }
    acc[result.type].push(result);
    return acc;
  }, {} as Record<string, SearchResult[]>);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query.trim() });
      setPage(1);
    }
  };

  const handleTypeChange = (type: SearchContentType) => {
    setSelectedType(type);
    setPage(1);
  };

  const handleSaveSearch = useCallback(async () => {
    if (!query.trim()) {
      toast.error('Please enter a search query');
      return;
    }

    const result = await searchService.saveSearch(
      `Search: ${query}`,
      query,
      selectedType === 'all' ? undefined : selectedType,
      filters
    );

    if (result.success) {
      toast.success('Search saved successfully');
    } else {
      toast.error(result.error?.getUserMessage() || 'Failed to save search');
    }
  }, [query, selectedType, filters]);

  const clearFilters = () => {
    setFilters({});
    setPage(1);
  };

  const hasActiveFilters = Object.keys(filters).length > 0;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-4">Search</h1>
          
          {/* Search Form */}
          <form onSubmit={handleSearch} className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
              <Input
                type="search"
                placeholder="Search for pages, news, events, and more..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-10 pr-4 text-lg h-12"
                aria-label="Search"
              />
            </div>
          </form>

          {/* Type Filter and Actions */}
          {query && (
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <Tabs value={selectedType} onValueChange={(v) => handleTypeChange(v as SearchContentType)}>
                <TabsList>
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="page">Pages</TabsTrigger>
                  <TabsTrigger value="news">News</TabsTrigger>
                  <TabsTrigger value="event">Events</TabsTrigger>
                  <TabsTrigger value="gallery">Gallery</TabsTrigger>
                  <TabsTrigger value="testimonial">Testimonials</TabsTrigger>
                  <TabsTrigger value="fact">Facts</TabsTrigger>
                </TabsList>
              </Tabs>
              
              <div className="flex gap-2">
                <Collapsible open={showFilters} onOpenChange={setShowFilters}>
                  <CollapsibleTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Filter className="w-4 h-4 mr-2" />
                      Filters
                      {hasActiveFilters && (
                        <span className="ml-2 bg-primary text-primary-foreground rounded-full px-2 py-0.5 text-xs">
                          {Object.keys(filters).length}
                        </span>
                      )}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2">
                    <Card className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="dateFrom">Date From</Label>
                          <Input
                            id="dateFrom"
                            type="date"
                            value={filters.dateFrom || ''}
                            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value || undefined })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="dateTo">Date To</Label>
                          <Input
                            id="dateTo"
                            type="date"
                            value={filters.dateTo || ''}
                            onChange={(e) => setFilters({ ...filters, dateTo: e.target.value || undefined })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="category">Category</Label>
                          <Input
                            id="category"
                            placeholder="Filter by category"
                            value={filters.category || ''}
                            onChange={(e) => setFilters({ ...filters, category: e.target.value || undefined })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="author">Author</Label>
                          <Input
                            id="author"
                            placeholder="Filter by author"
                            value={filters.author || ''}
                            onChange={(e) => setFilters({ ...filters, author: e.target.value || undefined })}
                          />
                        </div>
                      </div>
                      {hasActiveFilters && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearFilters}
                          className="mt-4"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Clear Filters
                        </Button>
                      )}
                    </Card>
                  </CollapsibleContent>
                </Collapsible>
                
                <Button variant="outline" size="sm" onClick={handleSaveSearch}>
                  <Save className="w-4 h-4 mr-2" />
                  Save Search
                </Button>
              </div>
            </div>
          )}

          {/* Search Suggestions */}
          {suggestions.length > 0 && query.length >= 2 && (
            <div className="mt-4 p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-2">Suggestions:</p>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((suggestion) => (
                  <Button
                    key={suggestion.id}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setQuery(suggestion.suggestion);
                      setSearchParams({ q: suggestion.suggestion });
                    }}
                  >
                    {suggestion.suggestion}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Saved Searches */}
          {savedSearches.length > 0 && user && (
            <div className="mt-4 p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-2">Your Saved Searches:</p>
              <div className="flex flex-wrap gap-2">
                {savedSearches.map((saved: SavedSearch) => (
                  <Button
                    key={saved.id}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setQuery(saved.query);
                      setSearchParams({ q: saved.query });
                      if (saved.contentType) {
                        setSelectedType(saved.contentType as SearchContentType);
                      }
                      if (saved.filters) {
                        setFilters(saved.filters);
                      }
                    }}
                  >
                    {saved.name}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        {!query && (
          <div className="text-center py-12">
            <Search className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Enter a search query to get started</p>
          </div>
        )}

        {query && isLoading && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Searching...</p>
          </div>
        )}

        {query && !isLoading && results.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No results found for "{query}"</p>
            <p className="text-sm text-muted-foreground mt-2">
              Try different keywords or check your spelling
            </p>
          </div>
        )}

        {query && !isLoading && results.length > 0 && (
          <div className="space-y-6">
            {/* Grouped Results */}
            {selectedType === 'all' ? (
              Object.entries(groupedResults).map(([type, typeResults]) => {
                const Icon = typeIcons[type] || FileText;
                return (
                  <div key={type} className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Icon className="w-5 h-5 text-heritage" />
                      <h2 className="text-xl font-semibold">{typeLabels[type] || type}</h2>
                      <span className="text-sm text-muted-foreground">
                        ({typeResults.length} {typeResults.length === 1 ? 'result' : 'results'})
                      </span>
                    </div>
                    <div className="grid gap-4">
                      {typeResults.map((result) => (
                        <Card key={`${result.type}-${result.id}`} className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <Link to={result.link} className="block">
                              <CardTitle className="text-lg mb-2 hover:text-heritage transition-colors">
                                {result.title}
                              </CardTitle>
                              <div className="text-sm text-muted-foreground line-clamp-2 mb-2">
                                <RichTextDisplay content={result.excerpt || ''} prose={false} />
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span className="capitalize">{result.type}</span>
                                <span>•</span>
                                <Link to={result.link} className="hover:text-heritage">
                                  View →
                                </Link>
                              </div>
                            </Link>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="grid gap-4">
                {results.map((result) => (
                  <Card key={`${result.type}-${result.id}`} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <Link to={result.link} className="block">
                        <CardTitle className="text-lg mb-2 hover:text-heritage transition-colors">
                          {result.title}
                        </CardTitle>
                        <div className="text-sm text-muted-foreground line-clamp-2 mb-2">
                          <RichTextDisplay content={result.excerpt || ''} prose={false} />
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="capitalize">{result.type}</span>
                          <span>•</span>
                          <Link to={result.link} className="hover:text-heritage">
                            View →
                          </Link>
                        </div>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Pagination */}
            {results.length >= limit && (
              <div className="flex justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="flex items-center px-4 text-sm text-muted-foreground">
                  Page {page}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setPage(p => p + 1)}
                  disabled={results.length < limit}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

